import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Slider } from "./slider"
import { AddButton, DeleteButton, IconButton } from "./buttons"
import { PillGroup } from "./pill"
import { EntryInput, EntryModal, EntrySelector, EntrySlider } from "./modal"
import { MultiEmptyView } from "./views"

/**
 * Validates display name for duplicate detection
 * @param {string} value - Display name to validate
 * @param {Array<string>} existingDisplayNames - Array of existing lowercase display names
 * @param {string} [currentDisplayName] - Current display name to exclude from validation (for editing)
 * @returns {{isValid: boolean, message: string|null}} Validation result
 */
const validateDisplayName = (value, existingDisplayNames, currentDisplayName = null) => {
  if (!value || !value.trim()) {
    return { isValid: false, message: "Display name is required" }
  }

  const trimmedValue = value.trim()
  const isDuplicate =
    existingDisplayNames?.some(
      name =>
        name.toLowerCase() === trimmedValue.toLowerCase() &&
        name !== currentDisplayName?.toLowerCase()
    ) || false

  return isDuplicate
    ? { isValid: false, message: "Display name already exists" }
    : { isValid: true, message: null }
}

export function Datasource({
  dataset,
  groups,
  sources,
  claimedColumnIds,
  existingDisplayNames,
  actions,
  usedTableIds,
}) {
  const [showNewModal, setShowNewModal] = useState(false)
  const [showCopyModal, setShowCopyModal] = useState(false)

  const setNewModalShow = useCallback(show => {
    return () => setShowNewModal(show)
  }, [])

  const handleOnAdd = useCallback(() => {
    setShowNewModal(() => true)
  }, [])

  const handleOnDelete = useCallback(() => {
    if (!dataset?.id) {
      return
    }

    actions.deleteMapping(dataset?.id)
  }, [actions, dataset?.id])

  const handleOnEnabled = useCallback(() => {
    if (!dataset?.id || dataset?.enabled == null) {
      return
    }

    actions.editMapping(dataset.id, { enabled: !dataset.enabled })
  }, [actions, dataset?.enabled, dataset?.id])

  const handleOnSaveColumn = useCallback(
    data => {
      if (!dataset?.id) {
        return
      }

      actions.addColumn(dataset?.id, data, () => {
        setShowNewModal(() => false)
      })
    },
    [actions, dataset?.id]
  )

  const columns = useMemo(() => {
    if (!dataset?.datasource?.id || !dataset?.table?.id || !sources?.length) {
      return null
    }

    for (const source of sources) {
      if (source.id !== dataset?.datasource?.id) {
        continue
      }

      for (const table of source.tables) {
        if (table.id === dataset?.table?.id) {
          const availableColumns = table.columns || []
          // Filter out columns that are already claimed by other datasets
          const filteredColumns = availableColumns.filter(
            col => !claimedColumnIds?.includes(col.id)
          )
          return filteredColumns
        }
      }
    }

    return null
  }, [dataset?.datasource?.id, dataset?.table?.id, sources, claimedColumnIds])

  const availableTables = useMemo(() => {
    if (!dataset?.datasource?.id || !sources?.length) {
      return []
    }

    for (const source of sources) {
      if (source.id === dataset.datasource.id) {
        return (source.tables || []).filter(
          t => t.id !== dataset?.table?.id && !usedTableIds?.includes(t.id)
        )
      }
    }

    return []
  }, [dataset?.datasource?.id, dataset?.table?.id, sources, usedTableIds])

  return (
    <div className="settings-user-management-user">
      <div className="settings-user-management-user-header">
        <div>
          <span>{dataset.name}</span>
        </div>
        <div>
          {actions.copyMapping != null && (
            <IconButton onClick={() => setShowCopyModal(true)} aria-label="Copy">
              Copy
            </IconButton>
          )}
          {actions.editMapping != null && (
            <Slider checked={dataset.enabled} onChecked={handleOnEnabled} />
          )}
          {/*<EditButton onClick={() => onEdit(datasource)}/>*/}
          {actions.deleteMapping != null && <DeleteButton onClick={handleOnDelete} />}
        </div>
      </div>
      <PillGroup className="flex-wrap-wrap">
        {dataset.columns?.length > 0 && (
          <>
            {dataset.columns.map(column => (
              <DatasourceMapping
                key={column.id}
                datasource={dataset}
                column={column}
                groups={groups}
                existingDisplayNames={existingDisplayNames}
                actions={actions}
              />
            ))}
            {actions.addColumn != null && (
              <AddColumn
                message="Add another column"
                disabled={!dataset.enabled}
                onClick={handleOnAdd}
              />
            )}
          </>
        )}
        {actions.addColumn != null && dataset.columns?.length === 0 && (
          <AddColumn
            message="Datasource has no Columns."
            disabled={!dataset.enabled}
            onClick={handleOnAdd}
          />
        )}
      </PillGroup>
      <MappingModal
        show={showNewModal}
        groups={groups}
        columns={columns}
        existingDisplayNames={existingDisplayNames}
        onHide={setNewModalShow(false)}
        onSave={handleOnSaveColumn}
      />
      {actions.copyMapping != null && (
        <CopyDatasetModal
          show={showCopyModal}
          tables={availableTables}
          onHide={() => setShowCopyModal(false)}
          onSave={targetTableId => {
            actions.copyMapping(dataset.id, targetTableId, () => setShowCopyModal(false))
          }}
        />
      )}
    </div>
  )
}

export function DatasourceMapping({ datasource, column, groups, existingDisplayNames, actions }) {
  const [timer, setTimer] = useState(null)
  const [data, setData] = useState(() => ({ ...column }))
  const [validation, setValidation] = useState({ isValid: true, message: null })
  const [validationTimer, setValidationTimer] = useState(null)
  const changeRef = useRef({})

  useEffect(() => {
    setData(() => ({ ...column }))
  }, [column])

  const validateDisplayNameDebounced = useCallback(
    value => {
      if (validationTimer) {
        clearTimeout(validationTimer)
      }

      const newTimer = setTimeout(() => {
        // For editing, exclude current item's display name from validation
        const currentDisplayName = column.display_name
        const result = validateDisplayName(value, existingDisplayNames, currentDisplayName)
        setValidation(result)
      }, 300) // 300ms debounce for real-time feedback

      setValidationTimer(newTimer)
    },
    [existingDisplayNames, validationTimer, column.display_name]
  )

  const handleOnChange = useCallback(
    key => {
      const formatValue = value => {
        if (typeof value === "string") {
          value = value.trim()
          if (value.length === 0) {
            value = null
          }
        }

        return value
      }

      return value => {
        if (!column.column?.id || !datasource?.id || !actions.editColumn) {
          return
        }

        if (typeof value === "object") {
          if (typeof value.value !== "object") {
            value = value.value
          } else {
            throw new Error(`unknown pattern ${value}`)
          }
        }

        setData(old => ({ ...old, [key]: value }))
        changeRef.current = {
          ...changeRef.current,
          [key]: formatValue(value),
        }

        // Trigger validation for display name changes
        if (key === "display_name") {
          validateDisplayNameDebounced(value)
        }

        const handler = () => {
          if (!Object.keys(changeRef.current).length) {
            return
          }

          actions.editColumn(datasource?.id, column.column?.id, changeRef.current)
          changeRef.current = {}
        }

        // Execute immediately for boolean values, use debounced timer for other values
        if (typeof value === "boolean") {
          handler()
        } else {
          if (timer) {
            clearTimeout(timer)
          }

          setTimer(() => setTimeout(handler, 1250))
        }
      }
    },
    [actions, column.column?.id, datasource?.id, timer, validateDisplayNameDebounced]
  )

  const handleOnDelete = useCallback(() => {
    if (!column.column?.id || !datasource?.id || !actions.deleteColumn) {
      return
    }

    actions.deleteColumn(datasource?.id, column.column?.id)
  }, [actions, column.column?.id, datasource?.id])

  const isDatasourceDisabled = !datasource.enabled

  return (
    <div className="settings-mappings-container full-height" aria-disabled={isDatasourceDisabled}>
      <div className="settings-mappings-header">
        <EntryInput
          className="flex-grow"
          editable={false}
          label="Column Name"
          value={data.column?.name}
        />
        <div className="row-flex gap-100 align-items-center">
          <Slider
            disabled={isDatasourceDisabled}
            checked={data.enabled}
            onChecked={handleOnChange("enabled")}
          />
          {actions.deleteColumn != null && <DeleteButton onClick={handleOnDelete} />}
        </div>
      </div>
      <div className="column-flex gap-200">
        <EntryInput
          label="Display Name"
          value={data.display_name}
          error={!validation.isValid}
          errorMessage={validation.message}
          onChange={handleOnChange("display_name")}
        />
        <EntrySelector
          creatable={true}
          label="Group"
          value={formatOption(data.group)}
          options={formatOptions(groups)}
          onChange={handleOnChange("group")}
        />
        <EntryInput
          className="flex-grow"
          label="Description"
          multi={true}
          value={data.description}
          onChange={handleOnChange("description")}
        />
        <EntrySelector
          isClearable={true}
          label="Format Option"
          value={formatOption(FORMAT_OPTIONS.find(o => o.value === data.format_options))}
          options={formatOptions(FORMAT_OPTIONS.filter(o => o.value !== data.format_options))}
          onChange={handleOnChange("format_options")}
        />
        <EntryInput
          className="flex-grow"
          label="Symbol"
          value={data.symbol}
          onChange={handleOnChange("symbol")}
        />
      </div>
      <div>
        <EntrySlider
          className="row-flex"
          label="Show in Map Popup"
          disabled={isDatasourceDisabled || !data.enabled}
          value={data.map_enabled}
          onChange={handleOnChange("map_enabled")}
        />
      </div>
    </div>
  )
}

function AddColumn({ message, disabled, onClick }) {
  return (
    <div
      className="settings-mappings-container full-height column-flex align-items-center justify-content-center"
      aria-disabled={disabled}
    >
      <MultiEmptyView>
        <span>{message}</span>
        <AddButton disabled={disabled} onClick={onClick}>
          Add Column
        </AddButton>
      </MultiEmptyView>
    </div>
  )
}

const DEFAULT_DATASOURCE_MODAL_DATA = {
  body: {
    name: null,
    datasource_id: null,
    table_id: null,
    enabled: false,
  },
  raw: {
    name: null,
    datasource: null,
    table: null,
    enabled: false,
  },
}

export function DatasourceModal({ show, datasource, sources, onHide, onSave }) {
  const [data, setData] = useState(DEFAULT_DATASOURCE_MODAL_DATA)
  const [tables, setTables] = useState([])

  useEffect(() => {
    if (!show && data) {
      setData(() => DEFAULT_DATASOURCE_MODAL_DATA)
      setTables(() => [])
    }
  }, [data, show])

  // useEffect(() => {
  //   if (datasource) {
  //     debugger
  //     setData(() => ({...datasource || DEFAULT_MODAL_DATA}))
  //   }
  // }, [datasource])

  const handleOnSave = useCallback(() => {
    onSave && onSave(data.body)
  }, [data, onSave])

  const handleNameChange = useCallback(name => {
    setData(old => ({
      ...old,
      body: { ...old.body, name: name !== null ? name.trim() : name },
      raw: { ...old.raw, name },
    }))
  }, [])

  const handleEnabledChange = useCallback(() => {
    setData(old => ({
      ...old,
      body: { ...old.body, enabled: !old.raw.enabled },
      raw: { ...old.raw, enabled: !old.raw.enabled },
    }))
  }, [])

  const handleSourceChange = useCallback(newValue => {
    setData(old => ({
      ...old,
      body: { ...old.body, datasource_id: newValue.value.id },
      raw: { ...old.raw, datasource: newValue },
    }))
    setTables(() => formatOptions(newValue.value.tables, "name"))
  }, [])

  const handleTableChange = useCallback(newValue => {
    setData(old => ({
      ...old,
      body: { ...old.body, table_id: newValue.value.id },
      raw: { ...old.raw, table: newValue },
    }))
  }, [])

  const isButtonDisabled = () => {
    return !data.body.name || !data.body.datasource_id || !data.body.table_id
  }

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide,
    },
    primary: {
      title: datasource == null ? "Add" : "Update",
      disabled: isButtonDisabled(),
      onClick: handleOnSave,
    },
  }

  return (
    <EntryModal
      bodyClassName="settings-user-management-new-user-modal"
      show={show}
      title={datasource == null ? "Add Datasource" : "Update Datasource"}
      buttons={buttons}
      onHide={onHide}
    >
      <div className="row-flex justify-content-space-between gap-500 align-items-center">
        <EntryInput
          className="flex-grow"
          label="Name"
          value={data.raw.name}
          autoFocus={true}
          onChange={handleNameChange}
        />
        <EntrySlider label="Enabled" value={data.raw.enabled} onChange={handleEnabledChange} />
      </div>
      <div className="column-flex gap-200">
        <EntrySelector
          label="Source"
          value={data.raw.datasource}
          options={formatOptions(
            sources.filter(s => s.id !== data.body.datasource_id),
            "name"
          )}
          onChange={handleSourceChange}
        />
        <EntrySelector
          disabled={!tables?.length}
          label="Table"
          value={data.raw.table}
          options={formatOptions(tables)}
          onChange={handleTableChange}
        />
      </div>
    </EntryModal>
  )
}

export function CopyDatasetModal({ show, tables, onHide, onSave }) {
  const [selectedTableId, setSelectedTableId] = useState(null)

  useEffect(() => {
    if (!show) {
      setSelectedTableId(null)
    }
  }, [show])

  const handleOnSave = useCallback(() => {
    if (selectedTableId) {
      onSave && onSave(selectedTableId)
    }
  }, [selectedTableId, onSave])

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide,
    },
    primary: {
      title: "Copy",
      disabled: !selectedTableId,
      onClick: handleOnSave,
    },
  }

  const selectedTable = tables?.find(t => t.id === selectedTableId)

  return (
    <EntryModal
      bodyClassName="settings-user-management-new-user-modal"
      show={show}
      title="Copy Dataset"
      buttons={buttons}
      onHide={onHide}
    >
      <EntrySelector
        label="Target Table"
        value={selectedTable ? formatOption(selectedTable, "name") : null}
        options={formatOptions(tables, "name")}
        onChange={value => {
          const id = value?.value?.id || value?.value || null
          setSelectedTableId(id)
        }}
      />
    </EntryModal>
  )
}

const DEFAULT_COLUMN_MODAL_DATA = {
  body: {
    column_id: null,
    display_name: null,
    group: null,
    enabled: false,
    description: null,
    format_options: null,
    symbol: null,
    map_enabled: false,
  },
  raw: {
    column: null,
    display_name: null,
    group: null,
    enabled: false,
    description: null,
    format_options: null,
    symbol: null,
    map_enabled: false,
  },
}

const FORMAT_OPTIONS = [
  { label: "Uppercase", value: "UPPER" },
  { label: "Lowercase", value: "LOWER" },
  { label: "Title", value: "TITLE" },
  { label: "Percentage", value: "PERCENTAGE" },
  { label: "Integer", value: "INT" },
]

export function MappingModal({ show, groups, columns, existingDisplayNames, onHide, onSave }) {
  const [data, setData] = useState(DEFAULT_COLUMN_MODAL_DATA)
  const [validation, setValidation] = useState({ isValid: true, message: null })
  const [validationTimer, setValidationTimer] = useState(null)

  useEffect(() => {
    if (!show) {
      setData(() => DEFAULT_COLUMN_MODAL_DATA)
      setValidation({ isValid: true, message: null })
      if (validationTimer) {
        clearTimeout(validationTimer)
        setValidationTimer(null)
      }
    }
  }, [show, validationTimer])

  const validateDisplayNameDebounced = useCallback(
    value => {
      if (validationTimer) {
        clearTimeout(validationTimer)
      }

      const newTimer = setTimeout(() => {
        const result = validateDisplayName(value, existingDisplayNames)
        setValidation(result)
      }, 300) // 300ms debounce for real-time feedback

      setValidationTimer(newTimer)
    },
    [existingDisplayNames, validationTimer]
  )

  const handleOnChange = useCallback(
    (rawKey, bodyKey = null) => {
      if (bodyKey == null) {
        bodyKey = rawKey
      }

      return value => {
        let bodyValue = value

        if (typeof bodyValue === "string") {
          if (!bodyValue.length) {
            bodyValue = null
          }

          if (rawKey === "description" && bodyValue != null) {
            bodyValue = bodyValue.trim()
            if (!bodyValue.length) {
              bodyValue = null
            }
          }
        }

        if (bodyValue != null && typeof bodyValue === "object") {
          if (typeof bodyValue.value === "string") {
            bodyValue = bodyValue.value
          } else if (typeof bodyValue.value?.id === "string") {
            bodyValue = bodyValue.value.id
          } else {
            bodyValue = null
          }
        }

        setData(old => ({
          ...old,
          body: { ...old.body, [bodyKey]: bodyValue },
          raw: { ...old.raw, [rawKey]: value },
        }))

        // Trigger validation for display name changes
        if (rawKey === "display_name") {
          validateDisplayNameDebounced(value)
        }
      }
    },
    [validateDisplayNameDebounced]
  )

  const handleOnSave = useCallback(() => {
    onSave && onSave(data.body)
  }, [data, onSave])

  if (!show) {
    return null
  }

  const isButtonDisabled = () => {
    return (
      !data.body.column_id || !data.body.display_name || !data.body.group || !validation.isValid
    )
  }

  const buttons = {
    secondary: {
      title: "Cancel",
      onClick: onHide,
    },
    primary: {
      title: "Add",
      disabled: isButtonDisabled(),
      onClick: handleOnSave,
    },
  }

  return (
    <EntryModal
      bodyClassName="settings-user-management-new-user-modal"
      show={show}
      title="Add Column"
      buttons={buttons}
      onHide={onHide}
    >
      <div className="row-flex justify-content-space-between gap-500 align-items-center">
        <EntrySelector
          label="Column"
          value={data.raw.column}
          options={formatOptions(columns, "name")}
          onChange={handleOnChange("column", "column_id")}
        />
        <EntrySlider
          label="Enabled"
          value={data.raw.enabled}
          onChange={handleOnChange("enabled")}
        />
      </div>
      <div className="column-flex gap-200">
        <EntryInput
          className="flex-grow"
          label="Display Name"
          value={data.raw.display_name}
          error={!validation.isValid}
          errorMessage={validation.message}
          onChange={handleOnChange("display_name")}
        />
        <EntrySelector
          creatable={true}
          label="Group"
          value={data.raw.group}
          options={formatOptions(groups)}
          onChange={handleOnChange("group")}
        />
        <EntryInput
          className="flex-grow"
          label="Description"
          multi={true}
          value={data.raw.description}
          onChange={handleOnChange("description")}
        />
        <EntrySelector
          isClearable={true}
          label="Format Option"
          value={data.raw.format_options}
          options={formatOptions(
            FORMAT_OPTIONS.filter(o => o.value !== data.raw.format_options?.value)
          )}
          onChange={handleOnChange("format_options")}
        />
        <EntryInput
          className="flex-grow"
          label="Symbol"
          value={data.raw.symbol}
          onChange={handleOnChange("symbol")}
        />
      </div>
      <div>
        <EntrySlider
          className="row-flex"
          label="Show in Map Popup"
          value={data.raw.map_enabled}
          onChange={handleOnChange("map_enabled")}
        />
      </div>
    </EntryModal>
  )
}

const formatOption = (option, key) => {
  if (option == null) return null

  if (typeof option == "string") {
    return { label: option, value: option }
  }

  if (option.label != null && option.value != null) {
    return option
  }

  return { label: option[key], value: option }
}

const formatOptions = (options, key) => {
  if (options == null) return null

  return options.map(o => formatOption(o, key))
}
